import dynamic from 'next/dynamic';
import MotionTooltip from '@components/MotionTooltip';
import { I_CallToAction } from '@containers/Home/types';
import { motion } from 'motion/react';
import { ElementType } from 'react';
import { memo, useState } from 'react';

const Fancybox = dynamic(() => import('./Fancybox'), { ssr: false });

interface Props {
  item: I_CallToAction;
  layoutVariant: 'panel' | 'list';
  position: 'left' | 'right';
  isPanelOpen: boolean;
  itemIndex: number;
}

const getTargetHref = (item: I_CallToAction) => {
  switch (item.typeValue) {
    case 'link':
      return item.value;
    case 'mailto':
      return `mailto:${item.value}`;
    case 'tel':
      return `tel:${item.value}`;
    default:
      return item.value;
  }
};

const CallToActionItem = memo(
  ({ item, layoutVariant, position, isPanelOpen, itemIndex }: Props) => {
    const [isShowFancybox, setIsShowFancybox] = useState(false);
    const scale = item.scale || 1;
    const isPanelVariant = layoutVariant === 'panel';
    const isLinkValue = item.typeValue === 'link';
    const LinkTag = (isLinkValue ? 'div' : 'a') as ElementType;

    const icon =
      item.typeIcon === 'image' ? item.icon : item.icon?.split('/')[1];

    const targetHref = getTargetHref(item);

    const onCloseFancybox = () => {
      setIsShowFancybox(false);
    };

    const onClickLink = () => {
      if (item.typeValue === 'link') {
        if (item?.action !== 'iframe')
          window.open(
            targetHref,
            item?.action === 'same-tab' ? '_self' : '_blank',
            item?.action === 'new-window'
              ? 'location=yes,height=570,width=520,scrollbars=yes,status=yes'
              : undefined,
          );
        else setIsShowFancybox(true);
      }
    };

    return (
      <>
        {isPanelVariant ? (
          <motion.div
            initial={false}
            animate={
              isPanelOpen
                ? { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
                : { opacity: 0, y: 8, scale: 0.96, filter: 'blur(4px)' }
            }
            transition={{
              duration: 0.28,
              ease: [0.22, 1, 0.36, 1],
              delay: isPanelOpen ? 0.14 + itemIndex * 0.06 : 0,
            }}
          >
            <LinkTag
              className={`group relative flex w-full max-h-30 max-w-50 cursor-pointer items-center gap-3 rounded-2xl bg-transparent p-1 transition-all duration-300 hover:bg-black/10!`}
              onClick={onClickLink}
              {...(isLinkValue ? {} : { target: '_blank', href: targetHref })}
            >
              {item.typeIcon === 'image' ? (
                <img
                  className={`inline-flex size-10! rounded-xl object-cover ring-1 ring-white/20 transition-all duration-300 group-hover:bg-black/20 group-hover:ring-transparent ${icon}`}
                  src={icon}
                  alt={item?.title}
                />
              ) : (
                <div className="inline-flex size-10 items-center justify-center rounded-xl ring-1 ring-white/20 transition-all duration-300 group-hover:bg-black/20 group-hover:ring-transparent">
                  <i aria-hidden className={`fas ${icon}`} />
                </div>
              )}
              <span className="max-w-50 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold">
                {item.title || ''}
              </span>
            </LinkTag>
          </motion.div>
        ) : (
          <MotionTooltip
            content={item.title || ''}
            position={position === 'left' ? 'right-center' : 'left-center'}
            overlayClassName="pointer-events-none"
            disabled={!item.title}
          >
            <LinkTag
              className="group relative flex w-max cursor-pointer items-center justify-center"
              onClick={onClickLink}
              {...(isLinkValue ? {} : { target: '_blank', href: targetHref })}
            >
              <div
                className="inline-flex aspect-square h-9 items-center justify-center rounded-full ring-1 ring-white/20 bg-[var(--color-primary)]! backdrop-blur-sm text-[var(--color-text)]! group-hover:ring-transparent transition-all duration-300 group-hover:scale-95"
                style={{ height: `${36 * scale}px` }}
              >
                {item.typeIcon === 'image' ? (
                  <img
                    className="object-cover rounded-full"
                    style={{
                      width: `${30 * scale}px`,
                      height: `${30 * scale}px`,
                    }}
                    src={icon}
                    alt={item?.title}
                  />
                ) : (
                  <i
                    aria-hidden
                    className={`${icon}`}
                    style={{ fontSize: `${14 * scale}px` }}
                  />
                )}
              </div>
            </LinkTag>
          </MotionTooltip>
        )}
        {isShowFancybox ? (
          <Fancybox src={targetHref} onClose={onCloseFancybox} />
        ) : null}
      </>
    );
  },
);

export default CallToActionItem;
